import { Button } from "@/components/ui/button";
import {
    DECYZJA_DO_KARTY_POBYTU,
    DECYZJA_DO_KARTY_POBYTU_ARTYKULY,
    DOSTEP_DO_RYNKU_PRACY,
    KARTA_POBYTU,
    LEGITYMACIA,
    OSWIADCZENIE,
    PASSPORT,
    PESEL_UKR,
    QUESTIONARY,
    STAMP,
    VISA,
    VISA_OPTIONS,
    ZASWIADCZENIE_Z_DZIEKANATY,
    ZEZWOLENIE_TYPY_A,
    ZGLOSZENIE_UKR
} from "@/constants";
import {
    editClientDocument,
    getContracts
} from "@/service/weexpertService";
import { format, isAfter } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { DatePicker } from "../ui/date-picker";
import { InputWithAlert } from "../ui/input-with-alert";
import { toast } from "../ui/use-toast";
import { BaseDialog } from "./dialog";
import { MetatdataInput } from "../documents/metadata.form";

const convertDates = (obj) => {
  if (Array.isArray(obj)) return obj.map(convertDates);

  if (obj && typeof obj === "object")
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        value instanceof Date
          ? format(value, "yyyy-MM-dd")
          : convertDates(value),
      ]),
    );

  return obj;
};

function doesTypeHaveValidPeriod(type, key) {
  const passportSchema = {
    validPeriod: true,
    documentId: true,
  };

  const questionarySchema = {
  };

  const visaSchema = {
    validPeriod: true,
    documentId: true,
    metadata: {
      visaType: {
        type: "select",
        options: VISA_OPTIONS,
      },
    },
  };
  const zezwolenie_typ_a = {
    validPeriod: true,
    documentId: true,
    metadata: {},
  };

  const oswidczenie = {
    validPeriod: true,
    documentId: true,
    metadata: {
      oswiadczenie_type: {
        type: "select",
        options: [
          {
            value: "UD",
            label: "UD",
          },
          {
            value: "UZ",
            label: "UZ",
          },
        ],
        required: true,
      },
      minimum_amount: {
        type: "input",
      },
      tytul: {
        type: "text",
      },
    },
  };


  const peselUkr = {
    validPeriod: false,
    documentId: false,
    metadata: {
      issued: {
        type: "date",
      },
    },
  };

  const zgloszenieUkr = {
    validPeriod: true,
    metadata: {
      zgloszenie_type: {
        type: "select",
        options: [
          {
            value: "UD",
            label: "UD",
          },
          {
            value: "UZ",
            label: "UZ",
          },
        ],
        required: true,
      },
    },
  };

  const kartaPobytu = {
    validPeriod: true,
    documentId: true,
  };

 const decyzjaDoKartyPobytu = {
    validPeriod: true,
    metadata: {
      art: {
        type: "select",
        options: DECYZJA_DO_KARTY_POBYTU_ARTYKULY,
      },
    },
  };

  const legitymacia = {
    validPeriod: true,
    documentId: false,
  };

  const stamp = {
    validPeriod: true,
    documentId: false,
  };
  const zaswiadczenie_z_dziekanatut = {
    validPeriod: true,
    documentId: false,
  };

  const dostep_do_rynku_pracy = {
    validPeriod: true,
    documentId: false,
  };

  switch (type) {
    case STAMP:
      return stamp[key] ?? false;
    case PASSPORT:
      return passportSchema[key] ?? false;
    case VISA:
      return visaSchema[key] ?? false;
    case ZEZWOLENIE_TYPY_A:
      return zezwolenie_typ_a[key] ?? false;
    case OSWIADCZENIE:
      return oswidczenie[key] ?? false;
    case ZASWIADCZENIE_Z_DZIEKANATY:
      return zaswiadczenie_z_dziekanatut[key] ?? false;
    case LEGITYMACIA:
      return legitymacia[key] ?? false;
    case PESEL_UKR:
      return peselUkr[key] ?? false;
    case KARTA_POBYTU:
      return kartaPobytu[key] ?? false;
    case DECYZJA_DO_KARTY_POBYTU:
      return decyzjaDoKartyPobytu[key] ?? false;
    case ZGLOSZENIE_UKR:
      return zgloszenieUkr[key] ?? false;
    case QUESTIONARY:
      return questionarySchema[key] ?? false;
    case DOSTEP_DO_RYNKU_PRACY:
      return dostep_do_rynku_pracy[key] ?? false;
    default:
      return false; //throw new Error("Type is now known to system")
  }
}

export default function ClientDocumentEditPopup({ doc }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const entityType = doc.entityType;
  const queryClient = useQueryClient();
  // valid from
  const [validFrom, setValidFrom] = useState(
    doesTypeHaveValidPeriod(entityType, "validPeriod")
      ? new Date(doc.validFrom)
      : new Date(),
  );
  const [validTo, setValidTo] = useState(
    doesTypeHaveValidPeriod(entityType, "validPeriod")
      ? new Date(doc.validTo)
      : new Date(),
  );
  const [documentId, setDocumnetId] = useState(
    doesTypeHaveValidPeriod(entityType, "documentId") ? doc.uniqueId : null,
  );
  const [metadata, setMetadata] = useState(
    doesTypeHaveValidPeriod(entityType, "metadata") ? doc.metadata : undefined,
  );
  const [inputChecked, setInputChecked] = useState(false);
  const [isButtonBlocked, setIsButtonBlocked] = useState(false);

  const handleEdit = () => {
    setInputChecked(true);
    if (!entityType) {
      return;
    }
    const haveValidPeriod = doesTypeHaveValidPeriod(entityType, "validPeriod");
    const haveUniqueueId = doesTypeHaveValidPeriod(entityType, "documentId");

    if (haveValidPeriod && (!validTo || !validTo)) {
      return;
    }

    if (haveUniqueueId && !documentId) {
      return;
    }
    setIsButtonBlocked(true);
    toast({
      title: "Starting uploading",
    });
    return editClientDocument(
      doc.id,
      documentId,
      haveValidPeriod ? format(validFrom, "yyyy-MM-dd") : null,
      haveValidPeriod ? format(validTo, "yyyy-MM-dd") : null,
      metadata ? convertDates(metadata) : {},
    )
      .then((res) => {
        toast({
          title: "Success",
        });
      })
      .catch((err) => {
        toast({
          title: "Error",
          variant: "destructive",
        });
      })
      .finally(() => {
        queryClient.invalidateQueries(["document"]);
        setOpen(false);
        setIsButtonBlocked(false);
      });
  };

  return (
    <BaseDialog
      dialogTitle={""}
      buttonText={t("common:button_change")}
      open={open}
      setOpen={setOpen}
    >
      <div className="w-[400px] p-1">
        <div className="pt-4">
          <InputWithAlert
            label={t("document_type")}
            disabled={true}
            type="text"
            value={doc.entityType}
            inputClassName={"h-[40px] w-full  mt-2"}
          />
        </div>
        {doesTypeHaveValidPeriod(entityType, "metadata") && (
          <MetatdataInput
            metadataSchema={doesTypeHaveValidPeriod(entityType, "metadata")}
            metadata={doc.metadata}
            onChange={(e) => setMetadata(e)}
          />
        )}
        {doesTypeHaveValidPeriod(entityType, "validPeriod") && (
          <div className="flex justify-between pt-4">
            <div className="w-[48%]">
              <DatePicker
                date={validFrom}
                defaultMonth={validFrom}
                setDate={(e) => setValidFrom(e)}
                error={
                  inputChecked && (!validFrom || isAfter(validFrom, validTo))
                }
                errorMessage={t("empty_data_invalid")}
                label={t("valid_from")}
              />
            </div>
            <div className="w-[48%]">
              <DatePicker
                date={validTo}
                defaultMonth={validTo}
                setDate={(e) => setValidTo(e)}
                error={
                  inputChecked && (!validTo || isAfter(validFrom, validTo))
                }
                errorMessage={t("empty_data_invalid")}
                label={t("valid_to")}
              />
            </div>
          </div>
        )}
        {doesTypeHaveValidPeriod(entityType, "documentId") && (
          <div className="w-full pt-4">
            <div>
              <InputWithAlert
                label={t("document_id")}
                value={documentId}
                onChange={(e) => {
                  setDocumnetId(e.target.value);
                }}
                inputClassName={"h-8"}
                error={
                  inputChecked &&
                  doesTypeHaveValidPeriod(entityType, "documentId") &&
                  !documentId
                }
                errorMessage={t("empty_data_invalid")}
              />
            </div>
          </div>
        )}
      </div>
      <div className="w-full flex justify-end pt-4">
        <Button disabled={isButtonBlocked} onClick={handleEdit}>
          {t("common:button_change")}
        </Button>
      </div>
    </BaseDialog>
  );
}
